import _regeneratorRuntime from 'babel-runtime/regenerator';

var _this = this;

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

function _asyncToGenerator(fn) { return function () { var gen = fn.apply(this, arguments); return new Promise(function (resolve, reject) { function step(key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { return Promise.resolve(value).then(function (value) { step("next", value); }, function (err) { step("throw", err); }); } } return step("next"); }); }; }

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import React, { useEffect, useMemo, useState } from 'react';
import { Search } from '@ellucian/react-design-system/core';
import { withEDSContext } from '../EDSContext/EDSContext';

import PropTypes from 'prop-types';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

/**
 * Get the display text of the dropdown.
 */
function getDisplayText(value, valueMap) {

    var label = void 0;

    if (!isNil(value) && !isEmpty(valueMap)) {
        if (isNil(valueMap[value])) {
            console.error('Unknown value: ' + value);
        } else {
            label = valueMap[value].props.label;
        }
    }

    return label;
}

/**
 * A dropdown that support typeahead item search.
 *
 * @done true
 * @updated true
 * @versionAdded v5.5.0
 * @examples
 *  DropdownTypeaheadExample
 *  DropdownTypeaheadRequiredExample
 *  DropdownTypeaheadIconExample
 */
var DropdownTypeahead = React.forwardRef(function (props, ref) {
    var value = props.value,
        onChangeProp = props.onChange,
        label = props.label,
        fullWidth = props.fullWidth,
        children = props.children,
        required = props.required,
        rest = _objectWithoutProperties(props, ['value', 'onChange', 'label', 'fullWidth', 'children', 'required']);

    var _useState = useState(),
        _useState2 = _slicedToArray(_useState, 2),
        dropdownItems = _useState2[0],
        setDropdownItems = _useState2[1];

    var _useState3 = useState({}),
        _useState4 = _slicedToArray(_useState3, 2),
        valueMap = _useState4[0],
        setValueMap = _useState4[1];

    var _useState5 = useState(),
        _useState6 = _slicedToArray(_useState5, 2),
        localValue = _useState6[0],
        setLocalValue = _useState6[1];

    var allChildren = useMemo(function () {
        return React.Children.toArray(children);
    }, [children]);

    useEffect(function () {

        setDropdownItems(allChildren);

        var map = allChildren.reduce(function (accumulator, val) {
            accumulator[val.props.value] = val;
            return accumulator;
        }, {});

        setValueMap(map);
    }, [allChildren]);

    useEffect(function () {
        setLocalValue(getDisplayText(value, valueMap));
    }, [value, valueMap]);

    function filter(searchValue, item) {
        return !item.props.disabled && item.props.label.toLowerCase().startsWith(searchValue.toLowerCase());
    }

    // TODO: We may need to optionally provide devs to implement this filter function
    var onFilter = function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(searchValue, categoryIndex, event) {
            var filterText, items;
            return _regeneratorRuntime.wrap(function _callee$(_context) {
                while (1) {
                    switch (_context.prev = _context.next) {
                        case 0:
                            filterText = searchValue.trim();
                            items = void 0;


                            if (isEmpty(filterText)) {
                                items = allChildren;
                            } else {
                                items = React.Children.toArray(children).filter(function (item) {
                                    return filter(filterText, item);
                                });
                            }

                            setDropdownItems(items);

                        case 4:
                        case 'end':
                            return _context.stop();
                    }
                }
            }, _callee, _this);
        }));

        return function onFilter(_x, _x2, _x3) {
            return _ref.apply(this, arguments);
        };
    }();

    var onClear = function onClear(event) {
        onChangeProp(undefined, event);
        setLocalValue(undefined);
        setDropdownItems(allChildren);
    };

    var onItemSelect = function onItemSelect(event, index, option) {
        onChangeProp(dropdownItems[index].props.value, event);
    };

    var onChange = function onChange(event) {
        setLocalValue(event.target.value);
    };

    return React.createElement(
        Search,
        Object.assign({
            onChange: onChange,
            ref: ref,
            onClear: onClear,
            value: localValue,
            label: required ? label + ' *' : label,
            fullWidth: fullWidth,
            isDropdown: true,
            placeholder: '',
            typeaheadOptions: {
                loading: false,
                onFilter: onFilter,
                onItemSelect: onItemSelect,
                searchDelay: 0
            }
        }, rest),
        dropdownItems
    );
});

DropdownTypeahead.muiName = 'DropdownTypeahead';

DropdownTypeahead.propTypes = {

    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,

    /**
     * The dropdown value. This corresponds to the `value` of the selected
     * `DropdownTypeaheadItem`.
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

    /**
     * Label that is attached to the `DropdownTypeahead`
     */
    label: PropTypes.string.isRequired,

    /**
     * Callback function fired when a dropdown item is selected.
     *
     * Signature:
     * ```
     * function(value: <any>, event: object) => void
     * ```
     * * `value`: The value that uniquely identifies the selected item. `undefined` if nothing is selected.
     * * `event`: The event source of the callback.
     */
    onChange: PropTypes.func,

    /**
     * If `true`, the dropdown will be disabled.
     */
    disabled: PropTypes.bool,

    /**
     * If `true`, the dropdown will display in an error state
     */
    error: PropTypes.bool,

    /**
     * If `true`, the dropdown will take up the full width of its container.
     */
    fullWidth: PropTypes.bool,

    /**
     * Text to be displayed by the FormHelperText component
     */
    helperText: PropTypes.string,

    /**
     * Id assigned to the input element and referenced by label element
     */
    id: PropTypes.string,

    /**
     * Marks the dropdown required
     */
    required: PropTypes.bool,

    /**
     * Size of the drodpdown. Valid values are 'medium' (the default) and 'small'.
     */
    size: PropTypes.string

};

DropdownTypeahead.defaultProps = {
    required: false,
    disabled: false,
    fullWidth: false,
    error: false,
    size: 'medium'
};

export default withEDSContext(DropdownTypeahead);